/**
 * Centralized DEX (Decentralized Exchange) configuration.
 * Single source of truth for all DEX-related mappings.
 *
 * To add a new DEX:
 * 1. Add entry to DEX_CONFIG
 * 2. Add to SUPPORTED_EXCHANGES in core/types/src/constants/supported-cex.ts
 * 3. Add logo file to apps/hub-v2/public/dexs/
 *
 * All other mappings are auto-derived from DEX_CONFIG.
 */

/**
 * DEX protocol identifiers
 */
export const DEX_PROTOCOLS = {
  UNISWAP_V3: "uniswap-v3",
  AERODROME_CL: "aerodrome-cl",
  AERODROME_CL_2: "aerodrome-cl-2",
  PANCAKE_V3: "pancake-v3",
  PANCAKE_V3_BASE: "pancake-v3-base",
  PANCAKE_INFINITY_BSC: "pancake-infinity-bsc",
  UNISWAP_V4_ROBINHOOD: "uniswap-v4-robinhood",
  // Solana DEXes
  METEORA: "meteora",
  METEORA_DYN: "meteora-dyn",
  METEORA_DYN2: "meteora-dyn2",
  METEORA_DBC: "meteora-dbc",
  RAYDIUM: "raydium",
  RAYDIUM_CLMM: "raydium-clmm",
  RAYDIUM_CPMM: "raydium-cpmm",
  RAYDIUM_LAUNCHLAB: "raydium-launchlab",
  ORCA: "orca",
  PUMPSWAP: "pumpswap",
  JUPITER_STUDIO: "jupiter-studio",
} as const;

export type DexProtocol = (typeof DEX_PROTOCOLS)[keyof typeof DEX_PROTOCOLS];

/**
 * Complete DEX configuration entry
 */
export interface DexConfigEntry {
  /** Protocol identifier (e.g., "aerodrome-cl") */
  protocol: DexProtocol;
  /** Display name (e.g., "Aerodrome") */
  name: string;
  /** Chain ID as string (e.g., "8453" for Base) */
  chainId: string;
  /** Chain name for display (e.g., "Base") */
  chainName: string;
  /** Mobula API blockchain name (e.g., "base") */
  mobulaChainName: string;
  /** Mobula Pulse API factory filter (e.g., "aerodrome") */
  mobulaFactory: string;
  /** Mobula API protocol ID for onlyProtocols filter (e.g., "pancake-v3") */
  mobulaProtocolId: string;
  /** Logo filename without extension (e.g., "aerodrome" -> /dexs/aerodrome.svg) */
  logoFile: string;
  /** Compound exchange ID format: {protocol}|{chain} (e.g., "aerodrome-cl|base") */
  exchangeId: string;
  /** Description for UI */
  description: string;
  /**
   * Selectable only from a campaign page (which pins `poolAddress`).
   * Mobula's `onlyProtocols` filter cannot route this protocol — it answers
   * "Naos failed to find route with specified protocols" — so an unpinned quote fails.
   */
  campaignOnly?: boolean;
}

/**
 * Central DEX configuration - single source of truth
 */
export const DEX_CONFIG: Record<DexProtocol, DexConfigEntry> = {
  [DEX_PROTOCOLS.UNISWAP_V3]: {
    protocol: DEX_PROTOCOLS.UNISWAP_V3,
    name: "Uniswap V3",
    chainId: "1",
    chainName: "Ethereum",
    mobulaChainName: "ethereum",
    // Mobula returns the raw factory address for Ethereum pools (no slug). This is the
    // canonical Uniswap V3 factory. NOTE: Mobula's Pulse `pools.factory.in` filter is
    // currently a no-op (verified across chains), so this value only documents intent.
    mobulaFactory: "0x1f98431c8ad98523631ae4a59f267346ea31f984",
    mobulaProtocolId: "uniswap-v3",
    logoFile: "uniswap",
    exchangeId: "uniswap-v3|ethereum",
    description: "The leading DEX on Ethereum",
  },
  [DEX_PROTOCOLS.AERODROME_CL]: {
    protocol: DEX_PROTOCOLS.AERODROME_CL,
    name: "Aerodrome CL",
    chainId: "8453",
    chainName: "Base",
    mobulaChainName: "base",
    mobulaFactory: "aerodrome",
    mobulaProtocolId: "aerodrome-cl",
    logoFile: "aerodrome",
    exchangeId: "aerodrome-cl|base",
    description: "Next-generation AMM on Base",
  },
  [DEX_PROTOCOLS.AERODROME_CL_2]: {
    protocol: DEX_PROTOCOLS.AERODROME_CL_2,
    name: "Aerodrome CL2",
    chainId: "8453",
    chainName: "Base",
    mobulaChainName: "base",
    mobulaFactory: "aerodrome",
    mobulaProtocolId: "aerodrome-cl-2",
    logoFile: "aerodrome",
    exchangeId: "aerodrome-cl-2|base",
    description: "Next-generation AMM on Base",
  },
  [DEX_PROTOCOLS.PANCAKE_V3]: {
    protocol: DEX_PROTOCOLS.PANCAKE_V3,
    name: "PancakeSwap V3",
    chainId: "42161",
    chainName: "Arbitrum",
    mobulaChainName: "arbitrum",
    mobulaFactory: "pancake-v3",
    mobulaProtocolId: "pancake-v3",
    logoFile: "pancakeswap",
    exchangeId: "pancake-v3|arbitrum",
    description: "The most popular DEX on Arbitrum",
  },
  [DEX_PROTOCOLS.PANCAKE_V3_BASE]: {
    protocol: DEX_PROTOCOLS.PANCAKE_V3_BASE,
    name: "PancakeSwap V3",
    chainId: "8453",
    chainName: "Base",
    mobulaChainName: "base",
    mobulaFactory: "pancake-v3",
    mobulaProtocolId: "pancake-v3",
    logoFile: "pancakeswap",
    exchangeId: "pancake-v3-base|base",
    description: "The most popular DEX on Base",
  },
  [DEX_PROTOCOLS.PANCAKE_INFINITY_BSC]: {
    protocol: DEX_PROTOCOLS.PANCAKE_INFINITY_BSC,
    name: "PancakeSwap Infinity",
    chainId: "56",
    chainName: "BNB Chain",
    mobulaChainName: "bsc",
    // Infinity pools are keyed by a bytes32 pool id, not a pool address, and hang off the
    // singleton vault rather than a per-pool factory contract.
    mobulaFactory: "pancakeswap-infinity",
    mobulaProtocolId: "pcs-infinity-cl",
    logoFile: "pancakeswap",
    exchangeId: "pancake-infinity-bsc|bsc",
    description: "The most popular DEX on BNB Chain",
    campaignOnly: true,
  },
  [DEX_PROTOCOLS.UNISWAP_V4_ROBINHOOD]: {
    protocol: DEX_PROTOCOLS.UNISWAP_V4_ROBINHOOD,
    name: "Uniswap V4",
    chainId: "4663",
    chainName: "Robinhood Chain",
    mobulaChainName: "robinhood-chain",
    // V4 pools are keyed by a bytes32 pool id and all live in the singleton PoolManager,
    // which Mobula reports as the factory (raw address, no slug).
    mobulaFactory: "0x8366a39cc670b4001a1121b8f6a443a643e40951",
    mobulaProtocolId: "uniswap-v4",
    logoFile: "uniswap",
    exchangeId: "uniswap-v4-robinhood|robinhood",
    description: "The leading DEX on Robinhood Chain",
    // `onlyProtocols=uniswap-v4` answers "Naos failed to find route" on chain 4663;
    // only a quote pinned to the pool id resolves.
    campaignOnly: true,
  },
  // Solana DEXes
  [DEX_PROTOCOLS.METEORA]: {
    protocol: DEX_PROTOCOLS.METEORA,
    name: "Meteora",
    chainId: "solana",
    chainName: "Solana",
    mobulaChainName: "solana",
    mobulaFactory: "meteora",
    mobulaProtocolId: "meteora",
    logoFile: "meteora",
    exchangeId: "meteora|solana",
    description: "Dynamic AMM on Solana",
  },
  [DEX_PROTOCOLS.METEORA_DYN]: {
    protocol: DEX_PROTOCOLS.METEORA_DYN,
    name: "Meteora Dynamic",
    chainId: "solana",
    chainName: "Solana",
    mobulaChainName: "solana",
    mobulaFactory: "meteora-dyn",
    mobulaProtocolId: "meteora-dyn",
    logoFile: "meteora",
    exchangeId: "meteora-dyn|solana",
    description: "Meteora Dynamic pools on Solana",
  },
  [DEX_PROTOCOLS.METEORA_DYN2]: {
    protocol: DEX_PROTOCOLS.METEORA_DYN2,
    name: "Meteora Dynamic V2",
    chainId: "solana",
    chainName: "Solana",
    mobulaChainName: "solana",
    mobulaFactory: "meteora-dyn2",
    mobulaProtocolId: "meteora-dyn2",
    logoFile: "meteora",
    exchangeId: "meteora-dyn2|solana",
    description: "Meteora Dynamic V2 pools on Solana",
  },
  [DEX_PROTOCOLS.METEORA_DBC]: {
    protocol: DEX_PROTOCOLS.METEORA_DBC,
    name: "Meteora DBC",
    chainId: "solana",
    chainName: "Solana",
    mobulaChainName: "solana",
    mobulaFactory: "meteora-dbc",
    mobulaProtocolId: "meteora-dbc",
    logoFile: "meteora",
    exchangeId: "meteora-dbc|solana",
    description: "Meteora DBC pools on Solana",
  },
  [DEX_PROTOCOLS.RAYDIUM]: {
    protocol: DEX_PROTOCOLS.RAYDIUM,
    name: "Raydium",
    chainId: "solana",
    chainName: "Solana",
    mobulaChainName: "solana",
    mobulaFactory: "raydium",
    mobulaProtocolId: "raydium",
    logoFile: "raydium",
    exchangeId: "raydium|solana",
    description: "Leading AMM on Solana",
  },
  [DEX_PROTOCOLS.RAYDIUM_CLMM]: {
    protocol: DEX_PROTOCOLS.RAYDIUM_CLMM,
    name: "Raydium CLMM",
    chainId: "solana",
    chainName: "Solana",
    mobulaChainName: "solana",
    mobulaFactory: "raydium-clmm",
    mobulaProtocolId: "raydium-clmm",
    logoFile: "raydium",
    exchangeId: "raydium-clmm|solana",
    description: "Raydium Concentrated Liquidity on Solana",
  },
  [DEX_PROTOCOLS.RAYDIUM_CPMM]: {
    protocol: DEX_PROTOCOLS.RAYDIUM_CPMM,
    name: "Raydium CPMM",
    chainId: "solana",
    chainName: "Solana",
    mobulaChainName: "solana",
    mobulaFactory: "raydium-cpmm",
    mobulaProtocolId: "raydium-cpmm",
    logoFile: "raydium",
    exchangeId: "raydium-cpmm|solana",
    description: "Raydium Constant Product AMM on Solana",
  },
  [DEX_PROTOCOLS.RAYDIUM_LAUNCHLAB]: {
    protocol: DEX_PROTOCOLS.RAYDIUM_LAUNCHLAB,
    name: "Raydium LaunchLab",
    chainId: "solana",
    chainName: "Solana",
    mobulaChainName: "solana",
    mobulaFactory: "raydium-launchlab",
    mobulaProtocolId: "raydium-launchlab",
    logoFile: "raydium",
    exchangeId: "raydium-launchlab|solana",
    description: "Raydium LaunchLab on Solana",
  },
  [DEX_PROTOCOLS.ORCA]: {
    protocol: DEX_PROTOCOLS.ORCA,
    name: "Orca",
    chainId: "solana",
    chainName: "Solana",
    mobulaChainName: "solana",
    mobulaFactory: "orca",
    mobulaProtocolId: "orca",
    logoFile: "orca",
    exchangeId: "orca|solana",
    description: "User-friendly DEX on Solana",
  },
  [DEX_PROTOCOLS.PUMPSWAP]: {
    protocol: DEX_PROTOCOLS.PUMPSWAP,
    name: "PumpSwap",
    chainId: "solana",
    chainName: "Solana",
    mobulaChainName: "solana",
    mobulaFactory: "pumpswap",
    mobulaProtocolId: "pumpswap",
    logoFile: "pumpswap",
    exchangeId: "pumpswap|solana",
    description: "DEX by Pump.fun on Solana",
  },
  [DEX_PROTOCOLS.JUPITER_STUDIO]: {
    protocol: DEX_PROTOCOLS.JUPITER_STUDIO,
    name: "Jupiter",
    chainId: "solana",
    chainName: "Solana",
    mobulaChainName: "solana",
    mobulaFactory: "jupiter-studio",
    mobulaProtocolId: "jupiter-studio",
    logoFile: "jupiter",
    exchangeId: "jupiter-studio|solana",
    description: "Leading swap aggregator on Solana",
  },
} as const;

// ============================================================================
// Auto-derived mappings (computed from DEX_CONFIG)
// ============================================================================

/**
 * All DEX config entries as array
 */
export const DEX_CONFIG_LIST = Object.values(DEX_CONFIG);

/**
 * DEXes offerable outside a campaign, where nothing pins `poolAddress`.
 * Use for pickers that enumerate DEXes; lookups by id/protocol must still use
 * {@link DEX_CONFIG_LIST} so campaign DEXes keep resolving.
 */
export const GENERAL_SWAP_DEX_CONFIG_LIST = DEX_CONFIG_LIST.filter((config) => !config.campaignOnly);

/**
 * Supported DEX chain IDs
 */
export const SUPPORTED_DEX_CHAIN_IDS = DEX_CONFIG_LIST.map((c) => c.chainId);

/**
 * Chain ID to Mobula blockchain name mapping (e.g., "8453" -> "base")
 */
export const CHAIN_ID_TO_MOBULA_CHAIN: Record<string, string> = Object.fromEntries(
  DEX_CONFIG_LIST.map((config) => [config.chainId, config.mobulaChainName]),
);

/**
 * Chain ID to Mobula factory mapping (e.g., "8453" -> "aerodrome")
 * @deprecated Multiple protocols can share the same chainId (e.g., Base has aerodrome-cl, aerodrome-cl-2, pancake-v3-base).
 * The last entry wins, causing silent data loss. Use {@link getMobulaFactory} when protocol is available.
 */
export const CHAIN_ID_TO_MOBULA_FACTORY: Record<string, string> = Object.fromEntries(
  DEX_CONFIG_LIST.map((config) => [config.chainId, config.mobulaFactory]),
);

/**
 * DEX protocol to logo filename mapping (e.g., "aerodrome-cl" -> "aerodrome")
 */
export const DEX_PROTOCOL_TO_LOGO: Record<string, string> = Object.fromEntries(
  DEX_CONFIG_LIST.map((config) => [config.protocol, config.logoFile]),
);

/**
 * Exchange ID to display label mapping (e.g., "aerodrome-cl|base" -> "Aerodrome (Base)")
 */
export const DEX_EXCHANGE_LABELS: Record<string, string> = Object.fromEntries(
  DEX_CONFIG_LIST.map((config) => [config.exchangeId, `${config.name} (${config.chainName})`]),
);

// ============================================================================
// Helper functions
// ============================================================================

/**
 * Get Mobula blockchain name by chain ID
 * @param chainId - Chain ID (e.g., "8453")
 * @returns Mobula blockchain name (e.g., "base") or undefined
 */
export const getMobulaChainByChainId = (chainId: string): string | undefined => {
  return CHAIN_ID_TO_MOBULA_CHAIN[chainId];
};

/**
 * Get DEX config by protocol
 * @param protocol - DEX protocol (e.g., "aerodrome-cl")
 * @returns DEX config or undefined
 */
export const getDexByProtocol = (protocol: string): DexConfigEntry | undefined => {
  return DEX_CONFIG[protocol as DexProtocol];
};

/**
 * Check if a chain ID is supported for DEX swaps
 * @param chainId - Chain ID to check
 * @returns true if supported
 */
export const isSupportedDexChain = (chainId: string): boolean => {
  return SUPPORTED_DEX_CHAIN_IDS.includes(chainId);
};

/**
 * Extract DEX protocol from exchange ID (e.g., "aerodrome-cl" from "aerodrome-cl|base")
 * @param exchangeId - Exchange ID in format {protocol}|{chain}
 * @returns Protocol part or undefined
 */
export const extractDexProtocol = (exchangeId: string): string | undefined => {
  return exchangeId.split("|")[0];
};

/**
 * Check if an exchange ID is a DEX (contains "|")
 * @param exchangeId - Exchange ID to check
 * @returns true if DEX
 */
export const isDexExchangeId = (exchangeId: string): boolean => {
  return exchangeId.includes("|");
};

/**
 * Get Mobula factory filter for a DEX protocol.
 * Unlike CHAIN_ID_TO_MOBULA_FACTORY (which has key collisions), this returns
 * the correct factory for a specific protocol.
 * @param protocol - DEX protocol (e.g., "pancake-v3-base")
 * @returns Mobula factory filter (e.g., "pancake-v3")
 */
export const getMobulaFactory = (protocol: DexProtocol): string => {
  return DEX_CONFIG[protocol].mobulaFactory;
};

/**
 * Get Mobula protocol ID for a DEX protocol.
 * Maps internal protocol IDs to Mobula's expected onlyProtocols values.
 * @param protocol - Internal DEX protocol (e.g., "pancake-v3-base")
 * @returns Mobula protocol ID (e.g., "pancake-v3")
 */
export const getMobulaProtocolId = (protocol: DexProtocol): string => {
  return DEX_CONFIG[protocol].mobulaProtocolId;
};
